import { useCallback, useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import { quotesByUserId } from 'graphql/queries';
import { flatConfigFromQuoteRow } from 'utils/quoteLoad';

export type SavedQuote = {
  quoteId: string;
  userId: string;
  description: string;
  createdAt: Date;
  config: Record<string, unknown>;
  metrics: Record<string, unknown>;
  raw: Record<string, unknown>;
};

/** The paged quotesByUserId loop from QuotesModal.js (lines 23-58), moved into
 *  a hook so the dialog is presentation. Same query, same paging, same
 *  newest-first ordering. */
export function useSavedQuotes(enabled: boolean) {
  const [quotes, setQuotes] = useState<SavedQuote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const client = generateClient();
      const { username } = await getCurrentUser();

      let items: Array<Record<string, any>> = [];
      let nextToken: string | null = null;
      do {
        const response: any = await client.graphql({
          query: quotesByUserId,
          variables: { userId: username, limit: 100, nextToken },
        });
        items = [...items, ...response.data.quotesByUserId.items];
        nextToken = response.data.quotesByUserId.nextToken;
      } while (nextToken);

      setQuotes(items
        .map((q) => ({
          quoteId: q.quoteId,
          userId: q.userId,
          description: q.description,
          createdAt: new Date(q.createdAt),
          config: flatConfigFromQuoteRow(q) as Record<string, unknown>,
          metrics: typeof q.metrics === 'string' ? JSON.parse(q.metrics) : (q.metrics ?? {}),
          raw: q,
        }))
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
    } catch (e) {
      console.error('Error loading quotes:', e);
      setError('Could not load your saved quotes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (enabled) void reload(); }, [enabled, reload]);

  return { quotes, loading, error, reload };
}
